import type { AvatarConfig } from './avatar';

export interface LegacyAvatarConfig {
  sex?: 'man' | 'woman';
  hairStyle?: 'normal' | 'thick' | 'mohawk' | 'womanLong' | 'womanShort';
  eyeStyle?: 'circle' | 'oval' | 'smile';
  noseStyle?: 'short' | 'long' | 'round';
  mouthStyle?: 'laugh' | 'smile' | 'peace';
  earSize?: 'small' | 'big';
  glassesStyle?: 'none' | 'round' | 'square';
  shirtStyle?: 'hoody' | 'short' | 'polo';
  hatStyle?: 'none' | 'beanie' | 'turban';
  hatColor?: string;
  hairColor?: string;
  faceColor?: string;
  shirtColor?: string;
  bgColor?: string;
  isGradient?: boolean;
}

export function mapLegacyConfigToDicebear(legacyConfig: LegacyAvatarConfig): AvatarConfig {
  return {
    top: mapHairStyle(legacyConfig.hairStyle, legacyConfig.hatStyle),
    eyes: mapEyeStyle(legacyConfig.eyeStyle),
    mouth: mapMouthStyle(legacyConfig.mouthStyle),
    accessories: mapGlassesStyle(legacyConfig.glassesStyle),
    clothing: mapShirtStyle(legacyConfig.shirtStyle),
    hairColor: legacyConfig.hairColor ? [legacyConfig.hairColor] : undefined,
    skinColor: legacyConfig.faceColor ? [legacyConfig.faceColor] : undefined,
    clothesColor: legacyConfig.shirtColor ? [legacyConfig.shirtColor] : undefined,
    backgroundColor: legacyConfig.bgColor ? [legacyConfig.bgColor] : undefined,
    backgroundType: legacyConfig.isGradient ? ['gradientLinear'] : ['solid'],
  };
}

function mapHairStyle(hairStyle?: string, hatStyle?: string): string[] {
  if (hatStyle === 'beanie') {
    return ['winterHat01'];
  }
  if (hatStyle === 'turban') {
    return ['turban'];
  }

  const mapping: Record<string, string[]> = {
    normal: ['shortFlat'],
    thick: ['curvy'],
    mohawk: ['shaggyMullet'],
    womanLong: ['longButNotTooLong'],
    womanShort: ['bob'],
  };

  return mapping[hairStyle || 'normal'] || ['shortFlat'];
}

function mapEyeStyle(eyeStyle?: string): string[] {
  const mapping: Record<string, string[]> = {
    circle: ['default'],
    oval: ['squint'],
    smile: ['happy'],
  };

  return mapping[eyeStyle || 'circle'] || ['default'];
}

function mapMouthStyle(mouthStyle?: string): string[] {
  const mapping: Record<string, string[]> = {
    laugh: ['smile'],
    smile: ['smile'],
    peace: ['serious'],
  };

  return mapping[mouthStyle || 'smile'] || ['smile'];
}

function mapGlassesStyle(glassesStyle?: string): string[] {
  if (glassesStyle === 'none') {
    return [];
  }

  const mapping: Record<string, string[]> = {
    round: ['round'],
    square: ['prescription02'],
  };

  return mapping[glassesStyle || 'none'] || [];
}

function mapShirtStyle(shirtStyle?: string): string[] {
  const mapping: Record<string, string[]> = {
    hoody: ['hoodie'],
    short: ['shirtCrewNeck'],
    polo: ['collarAndSweater'],
  };

  return mapping[shirtStyle || 'hoody'] || ['hoodie'];
}

export function mapDicebearToLegacyConfig(dicebearConfig: AvatarConfig): LegacyAvatarConfig {
  return {
    hairStyle: mapTopToHairStyle(dicebearConfig.top?.[0]),
    eyeStyle: mapEyesToEyeStyle(dicebearConfig.eyes?.[0]),
    mouthStyle: mapMouthToMouthStyle(dicebearConfig.mouth?.[0]),
    glassesStyle: mapAccessoriesToGlassesStyle(dicebearConfig.accessories?.[0]),
    shirtStyle: mapClothingToShirtStyle(dicebearConfig.clothing?.[0]),
    hatStyle: mapTopToHatStyle(dicebearConfig.top?.[0]),
    hairColor: dicebearConfig.hairColor?.[0],
    faceColor: dicebearConfig.skinColor?.[0],
    shirtColor: dicebearConfig.clothesColor?.[0],
    bgColor: dicebearConfig.backgroundColor?.[0],
    isGradient: dicebearConfig.backgroundType?.[0] === 'gradientLinear',
  };
}

function mapTopToHairStyle(top?: string): LegacyAvatarConfig['hairStyle'] {
  const mapping: Record<string, LegacyAvatarConfig['hairStyle']> = {
    shortFlat: 'normal',
    shortRound: 'normal',
    curvy: 'thick',
    curly: 'thick',
    shaggyMullet: 'mohawk',
    sides: 'mohawk',
    longButNotTooLong: 'womanLong',
    straight02: 'womanLong',
    bob: 'womanShort',
    miaWallace: 'womanShort',
  };

  return mapping[top || 'shortFlat'] || 'normal';
}

function mapTopToHatStyle(top?: string): LegacyAvatarConfig['hatStyle'] {
  if (top?.includes('winterHat')) {
    return 'beanie';
  }
  if (top === 'turban') {
    return 'turban';
  }
  return 'none';
}

function mapEyesToEyeStyle(eyes?: string): LegacyAvatarConfig['eyeStyle'] {
  const mapping: Record<string, LegacyAvatarConfig['eyeStyle']> = {
    default: 'circle',
    squint: 'oval',
    happy: 'smile',
  };

  return mapping[eyes || 'default'] || 'circle';
}

function mapMouthToMouthStyle(mouth?: string): LegacyAvatarConfig['mouthStyle'] {
  const mapping: Record<string, LegacyAvatarConfig['mouthStyle']> = {
    smile: 'smile',
    serious: 'peace',
    laugh: 'laugh',
  };

  return mapping[mouth || 'smile'] || 'smile';
}

function mapAccessoriesToGlassesStyle(accessory?: string): LegacyAvatarConfig['glassesStyle'] {
  if (!accessory) {
    return 'none';
  }

  const mapping: Record<string, LegacyAvatarConfig['glassesStyle']> = {
    round: 'round',
    prescription01: 'square',
    prescription02: 'square',
    wayfarers: 'square',
  };

  return mapping[accessory] || 'none';
}

function mapClothingToShirtStyle(clothing?: string): LegacyAvatarConfig['shirtStyle'] {
  const mapping: Record<string, LegacyAvatarConfig['shirtStyle']> = {
    hoodie: 'hoody',
    shirtCrewNeck: 'short',
    shirtScoopNeck: 'short',
    collarAndSweater: 'polo',
  };

  return mapping[clothing || 'hoodie'] || 'hoody';
}
